import { ScoreDisplay } from "@/ai-coding-outputs/ScoreDisplay";
import { useReviewNotesManager } from "@/ai-coding-outputs/human-coding/useReviewNotesManager";
import { ROUTES } from "@/ai-coding-outputs/navigation";
import { useCustomerFilter } from "@/ai-coding-outputs/useCustomerFilter";
import { useSkipEncountersFilter } from "@/ai-coding-outputs/useSkipEncountersFilter";
import { useAuth } from "@/auth/AuthProvider";
import { USER_ROLES } from "@/auth/userRoles";
import type { ClinicalCode, CodeType } from "@/clinical-codes/types/ClinicalCodeTypes";
import { getOpcsVersionForDate, getOpcsVersionLabel } from "@/clinical-codes/opcsVersion";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfirmationDialog } from "@/components/ui/ConfirmationDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MultiSelect, type Option } from "@/components/ui/multi-select";
import { ResizeHandle } from "@/components/ui/ResizeHandle";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useResizableColumn } from "@/components/ui/useResizableColumn";
import { getReviewPageBackground } from "@/customers/reviewPageColours";
import { useCustomerChangeAlert } from "@/customers/useCustomerChangeAlert";
import { useCustomers } from "@/customers/useCustomers";
import { usePageHeaderSlot } from "@/layout/PageHeaderSlotContext";
import { useProcessedFiles } from "@/inbound-data/useProcessedFiles";
import { supabase } from "@/integrations/supabase/client";
import { DragDropContext, Draggable, Droppable, type DropResult } from "@hello-pangea/dnd";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  CheckCircle,
  ChevronDown,
  Clock,
  Home,
  History,
  Microscope,
  ScrollText,
  Trash2,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { toast } from "sonner";
import { getMastraWorkflowRunUrl } from "@/ai-coding-outputs/mastraWorkflowRunUrl";
import dayjs from "dayjs";
import { ViewRunDropdown } from "@/ai-coding-outputs/ViewRunDropdown";
import ProcessedFilesTabs from "../ProcessedFilesTabs";
import { TrustRulesButton } from "../TrustRulesButton";
import { useEvidenceHighlighting } from "../useEvidenceHighlighting";
import { useIndicationVerdicts } from "../useIndicationVerdicts";
import { uniqueQuoteTexts } from "../evidenceUtils";
import AddClinicalCodeDialog from "./AddClinicalCodeDialog";
import { clampMs, trackAuditEvent } from "@/api/auditEvents";
import { CodeRuleAuditDialog } from "./CodeRuleAuditDialog";
import { DoNotOutputModal } from "./DoNotOutputModal";
import { InlineLoading, LoadingOverlay, SkeletonCard } from "./LoadingOverlay";
import { ReviewNotes } from "./ReviewNotes";
import { OpenRawFilesButton } from "@/raw-files-viewer/OpenRawFilesButton";
import { QueueAlmostThroughPill } from "./QueueAlmostThroughPill";
import { ReviewStateBadge } from "./ReviewStateBadge";
import { SaveStateBadge } from "./SaveStateBadge";
import { OrphanedAsteriskCheckFailedNotice, RestrictionCheckFailedNotice } from "../PrimaryDiagnosisWarning";
import { InlineFlags, procedureInlineFlags, historicCodeSet, useInlineDiagnosisFlags } from "../inlineCodeFlags";
import { DiagnosisTimeline } from "./DiagnosisTimeline";
import { dockStatus } from "./auditDockModel";
import { useAutoSaveReviewCodes } from "./useAutoSaveReviewCodes";
import { useBackgroundCodeRuleAudit } from "./useBackgroundCodeRuleAudit";
import { useDiagnosisEvidence } from "./useDiagnosisEvidence";
import { useDroppedCodesToggle } from "./useDroppedCodesToggle";
import { useEncounterHumanReviewMutations } from "./useEncounterHumanReviewMutations";
import { HumanReviewNotFoundError, useEncounterHumanCodingReviewData } from "./useHumanCodingReviewData";
import { useNextUnreviewedEncounter } from "./useNextUnreviewedEncounter";
import { useRecentReviewedEncounters } from "./useRecentReviewedEncounters";
import { InsertionPointButton } from "./InsertionPointButton";
import { DiagnosisSourceText } from "./DiagnosisSourceText";
import { InlineCodeSearch } from "./InlineCodeSearch";
import { useUnmatchedLeadTerms } from "./useUnmatchedLeadTerms";
import { UnmatchedLeadTermItem } from "./UnmatchedLeadTermItem";

type Diagnosis = {
  code: string;
  description: string;
  orderIndex: number;
  id?: string;
};

type Procedure = {
  code: string;
  description: string;
  orderIndex: number;
  id?: string;
};

// Radix wraps ScrollArea content in a display:table div that widens to the longest line instead of wrapping.
const RADIX_SCROLL_CONTENT_BLOCK = "[&>[data-radix-scroll-area-viewport]>div]:!block";

const HumanCodingReview = ({
  encounterId,
  reviewId,
  classificationResultId,
}: {
  encounterId: string;
  reviewId: string;
  classificationResultId: string;
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromAdminReview = searchParams.get("fromAdminReview") === "true";
  const { data, isLoading, error } = useEncounterHumanCodingReviewData(encounterId, classificationResultId);
  const { data: unmatchedLeadTerms } = useUnmatchedLeadTerms(classificationResultId);
  const { data: indications } = useIndicationVerdicts(encounterId);

  const { role } = useAuth();
  const canAuditCodes = role === USER_ROLES.admin || role === USER_ROLES.clinical_coder_admin;
  const { customerIds, setCustomerIds } = useCustomerFilter();
  const { skipEncounterIds, addSkipEncounterId } = useSkipEncountersFilter();
  const { customers } = useCustomers();
  const pageHeaderSlot = usePageHeaderSlot();
  const { data: recentReviewedEncounters } = useRecentReviewedEncounters({
    excludeEncounterId: encounterId,
  });
  const { isFlashing } = useCustomerChangeAlert(data?.customer?.provider_id ?? null);

  // Handler for MultiSelect change
  const handleCustomerFilterChange = (options: Option[]) => {
    setCustomerIds(options.map((o) => o.value));
  };

  // Next unreviewed item hook for navigation (disabled by default)
  const { refetchWithExcludeIds } = useNextUnreviewedEncounter({
    enabled: false,
    excludeIds: encounterId ? [encounterId, ...skipEncounterIds] : [...skipEncounterIds],
    customerIds:
      role === USER_ROLES.admin
        ? customerIds.length > 0
          ? customerIds
          : customers.map((c) => c.id)
        : customers.map((c) => c.id),
  });

  // Review notes management
  const saveNotesMutation = useReviewNotesManager({
    reviewId,
  });
  // Store mutateAsync in a ref to keep it stable across renders
  // Update ref synchronously on every render to always have latest function
  const saveNotesMutateAsyncRef = useRef(saveNotesMutation.mutateAsync);
  saveNotesMutateAsyncRef.current = saveNotesMutation.mutateAsync;

  // State for code manipulation
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [diagnoses, setDiagnoses] = useState<Diagnosis[]>([]);
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  // State for tracking reviewed codes
  const [reviewedCodeIds, setReviewedCodeIds] = useState<Set<string>>(new Set());
  // Add code dialog state moved into AddClinicalCodeDialog
  const [showDoNotOutputModal, setShowDoNotOutputModal] = useState(false);
  const [showFinalizeConfirm, setShowFinalizeConfirm] = useState(false);
  const [showAICodesDialog, setShowAICodesDialog] = useState(false);
  const [insertionPoint, setInsertionPoint] = useState<{
    section: "procedures" | "diagnoses";
    index: number;
  } | null>(null);
  const [showNavigationConfirm, setShowNavigationConfirm] = useState(false);
  const [pendingHistoricCodeDelete, setPendingHistoricCodeDelete] = useState<{ code: string; index: number } | null>(
    null,
  );
  const [startTime] = useState(Date.now());
  const opcsVersion = useMemo(
    () => (data ? getOpcsVersionForDate(data.dischargeDatetime) : undefined),
    [data?.dischargeDatetime],
  );
  const allowNavigationRef = useRef(false);
  const pendingNavigationRef = useRef<string | null>(null);
  // Radix closes the dialog after its confirm handler runs, so the close path must not resume the
  // autosave the in-flight finalise is still suspending.
  const finalizeConfirmedRef = useRef(false);

  const pdfColumn = useResizableColumn({
    initial: 70,
    min: 30,
    max: 85,
    storageKey: "human-coding-pdf-column-width",
    unit: "percent",
  });

  const { data: evidenceData } = useDiagnosisEvidence(classificationResultId);
  const {
    timelinePanel,
    allHighlights,
    activeFileIdentifier,
    scrollTrigger,
    evidenceByCode,
    evidenceHighlight,
    handleOpenTimeline,
    handleCloseTimeline,
    handleToggleHighlight,
    handleTimelineEvidenceClick,
  } = useEvidenceHighlighting(evidenceData, unmatchedLeadTerms);
  const { showDropped, toggleDropped } = useDroppedCodesToggle();
  const latestDiagnoses = data?.latestClassificationResult.diagnoses;
  const historicCodes = useMemo(() => historicCodeSet(latestDiagnoses), [latestDiagnoses]);
  const incorrectPrimaryCode =
    data?.latestClassificationResult.endoscopyResult?.primaryDiagnosisIdentified === false
      ? (latestDiagnoses?.[0]?.code ?? null)
      : null;
  const { flagsByIndex, daggerAsteriskCheckFailed, restrictionCheckFailed } = useInlineDiagnosisFlags({
    diagnoses,
    specialty: data?.specialty,
    patientAge: data?.patientAge,
    patientSex: data?.patientSex,
    historicCodes,
    incorrectPrimaryCode,
  });

  // Handle browser tab/window close
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Intercept browser back/forward buttons and link clicks
  useEffect(() => {
    const handlePopState = () => {
      if (!allowNavigationRef.current) {
        // Push current state back to prevent navigation
        window.history.pushState(null, "", window.location.href);
        setShowNavigationConfirm(true);
      }
    };

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;

      if (anchor && anchor.target !== "_blank" && !allowNavigationRef.current) {
        const href = anchor.getAttribute("href");
        // Only intercept internal links (not external links or anchors)
        if (
          href &&
          !href.startsWith("http") &&
          !href.startsWith("mailto:") &&
          !href.startsWith("#") &&
          href !== window.location.pathname
        ) {
          e.preventDefault();
          e.stopPropagation();
          pendingNavigationRef.current = href;
          setShowNavigationConfirm(true);
        }
      }
    };

    // Push a state to enable popstate detection
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handlePopState);
    document.addEventListener("click", handleClick, true);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      document.removeEventListener("click", handleClick, true);
    };
  }, [encounterId]);

  // Reset codes state when navigating to a different case
  useEffect(() => {
    setProcedures([]);
    setDiagnoses([]);
    setReviewedCodeIds(new Set());
  }, [encounterId]);

  // Helper function to ensure a code has an ID
  const ensureCodeId = <T extends Procedure | Diagnosis>(code: T): T & { id: string } => {
    if (code.id) {
      return code as T & { id: string };
    }
    return { ...code, id: crypto.randomUUID() };
  };

  // Keyed on the code arrays, not `data`: a status-only cache write would otherwise discard unsaved edits.
  useEffect(() => {
    const loadedProcedures = (data?.procedures || []).map((proc) => ensureCodeId(proc as Procedure)) as Procedure[];
    const loadedDiagnoses = (data?.diagnoses || []).map((diag) => ensureCodeId(diag as Diagnosis)) as Diagnosis[];
    setProcedures(loadedProcedures);
    setDiagnoses(loadedDiagnoses);
  }, [data?.procedures, data?.diagnoses]);

  // Human review mutations
  const { finalizeCodes, rejectOutput, isFinalizingCodes, isProcessingDoNotOutput } =
    useEncounterHumanReviewMutations();

  // Debounced autosave of code lists
  const { saveState, isSaveInFlight, suspendAutosave, resumeAutosave } = useAutoSaveReviewCodes({
    encounterId,
    diagnoses,
    procedures,
    baselineDiagnoses: data?.diagnoses,
    baselineProcedures: data?.procedures,
    debounceMs: 1000,
  });

  const auditState = useBackgroundCodeRuleAudit({ enabled: canAuditCodes, encounterId, diagnoses, procedures });
  const checkStartedAtRef = useRef<number | null>(null);
  // Screened-clear results share one object, so identity can't tell two checks apart.
  const reportedResultRef = useRef<{ key: string | null; at: number | null } | null>(null);
  useEffect(() => {
    if (!canAuditCodes) return;
    if (auditState.phase === "checking") {
      checkStartedAtRef.current ??= performance.now();
      return;
    }
    const { result, resultKey, resultAt } = auditState;
    if (auditState.phase !== "ready" || !result) {
      checkStartedAtRef.current = null;
      return;
    }
    const startedAt = checkStartedAtRef.current;
    checkStartedAtRef.current = null;
    const reported = reportedResultRef.current;
    if (reported?.key === resultKey && reported?.at === resultAt) return;
    reportedResultRef.current = { key: resultKey, at: resultAt };
    const { tone, highCount, mediumCount } = dockStatus(auditState);
    if (tone === "idle" || tone === "checking" || tone === "failed") return;
    trackAuditEvent({
      type: "audit_result_ready",
      encounterId,
      tone,
      highCount,
      mediumCount,
      checkMs: startedAt === null ? 0 : clampMs(performance.now() - startedAt),
      screened: auditState.screened,
    });
  }, [auditState, canAuditCodes, encounterId]);

  // Analytics tracking
  const trackTimeSpent = useCallback(async () => {
    const timeSpent = Math.floor((Date.now() - startTime) / 1000);
    console.log(`Encounter ${encounterId} reviewed in ${timeSpent} seconds`);
  }, [encounterId, startTime]);

  const handleHome = useCallback(() => {
    allowNavigationRef.current = true;
    trackTimeSpent();
    navigate(ROUTES.aiCodingOutputs.route);
  }, [navigate, trackTimeSpent]);

  const handleGotoNext = useCallback(async () => {
    if (!encounterId) return;

    allowNavigationRef.current = true;
    const nextItem = await refetchWithExcludeIds([encounterId, ...skipEncounterIds]);

    if (nextItem?.encounterId) {
      const url = `${ROUTES.humanCodingReview.route}/${nextItem.encounterId}${nextItem.classificationResultId ? `?classificationResultId=${nextItem.classificationResultId}` : ""}`;
      navigate(url);
    } else {
      // No more items - navigate to worklist
      navigate(ROUTES.aiCodingOutputs.route);
    }
  }, [encounterId, skipEncounterIds, refetchWithExcludeIds, navigate]);

  const handleSkip = useCallback(async () => {
    if (!encounterId) return;

    allowNavigationRef.current = true;
    // Add current ID to skip list
    addSkipEncounterId(encounterId);

    // Refetch with updated excludeIds (including the newly added skip ID)
    const nextItem = await refetchWithExcludeIds([encounterId, ...skipEncounterIds]);

    if (nextItem?.encounterId) {
      const url = `${ROUTES.humanCodingReview.route}/${nextItem.encounterId}${nextItem.classificationResultId ? `?classificationResultId=${nextItem.classificationResultId}` : ""}`;
      navigate(url);
    } else {
      // No more items - navigate to worklist
      navigate(ROUTES.aiCodingOutputs.route);
    }
  }, [encounterId, addSkipEncounterId, skipEncounterIds, refetchWithExcludeIds, navigate]);

  // Fetch processed files for this case
  const { data: reportFiles, isLoading: isLoadingFiles, error: fileError } = useProcessedFiles(classificationResultId);

  const openFinalizeConfirm = () => {
    finalizeConfirmedRef.current = false;
    // The debounce would otherwise elapse while the dialog is open and rewrite the codes the finalise approves.
    suspendAutosave();
    setShowFinalizeConfirm(true);
  };

  const dismissFinalizeConfirm = () => {
    setShowFinalizeConfirm(false);
    if (finalizeConfirmedRef.current) return;

    resumeAutosave();
  };

  const handleFinalize = async () => {
    if (!encounterId) return;

    const acceptedCodes = {
      procedures: procedures.map((proc) => ({ code: proc.code, description: proc.description })),
      diagnoses: diagnoses.map((diag) => ({ code: diag.code, description: diag.description })),
    };

    suspendAutosave();

    try {
      await finalizeCodes.mutateAsync({
        encounterId,
        procedures: acceptedCodes.procedures.map((proc, index) => ({
          opcs_4_code: proc.code,
          description: proc.description,
          order_index: index,
        })),
        diagnoses: acceptedCodes.diagnoses.map((diag, index) => ({
          icd_10_code: diag.code,
          description: diag.description,
          order_index: index,
        })),
      });
    } catch (err) {
      resumeAutosave();
      console.error("Failed to finalize codes:", err);
      return;
    }

    resumeAutosave({ acceptedCodes });

    allowNavigationRef.current = true;
    try {
      if (fromAdminReview) {
        navigate(`${ROUTES.unwrittenEncounterReview.route}/${encounterId}`);
      } else {
        await handleGotoNext();
      }
    } catch (err) {
      console.error("Failed to open the next encounter after finalizing codes:", err);
    }
  };

  const confirmFinalize = () => {
    setShowFinalizeConfirm(false);
    if (canAuditCodes && encounterId) {
      const { tone, highCount } = dockStatus(auditState);
      trackAuditEvent({ type: "finalise_confirmed", encounterId, tone, highCount });
    }
    finalizeConfirmedRef.current = true;
    handleFinalize();
  };

  const handleDoNotOutput = () => {
    setShowDoNotOutputModal(true);
  };

  const handleDoNotOutputConfirm = async (reason: string, notes?: string) => {
    if (!encounterId) return;

    await trackTimeSpent();

    await rejectOutput.mutateAsync({
      encounterId,
      reason,
      notes,
    });

    setShowDoNotOutputModal(false);

    allowNavigationRef.current = true;
    if (fromAdminReview) {
      navigate(`${ROUTES.unwrittenEncounterReview.route}/${encounterId}`);
    } else {
      await handleGotoNext();
    }
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const { source, destination, type } = result;

    if (type === "procedures") {
      const newProcedures = Array.from(procedures);
      const [reorderedItem] = newProcedures.splice(source.index, 1);
      newProcedures.splice(destination.index, 0, reorderedItem);

      // Update order_index for each procedure
      const updatedProcedures = newProcedures.map((proc, index) => ({
        ...proc,
        orderIndex: index + 1,
      }));

      setProcedures(updatedProcedures);
    } else if (type === "diagnoses") {
      const newDiagnoses = Array.from(diagnoses);
      const [reorderedItem] = newDiagnoses.splice(source.index, 1);
      newDiagnoses.splice(destination.index, 0, reorderedItem);

      // Update order_index for each diagnosis
      const updatedDiagnoses = newDiagnoses.map((diag, index) => ({
        ...diag,
        orderIndex: index + 1,
      }));

      setDiagnoses(updatedDiagnoses);
    }
  };

  const handleDeleteProcedure = (code: string, index: number) => {
    // there could be multiple procedures with the same code, so we need to delete at position.
    setProcedures((prev) => {
      const procedure = prev[index];
      if (!procedure) {
        return prev;
      }

      if (procedure.code !== code) {
        return prev;
      }

      // Remove from reviewed codes if it was reviewed
      const procedureId = procedure.id || `${procedure.code}-${index}`;
      setReviewedCodeIds((prevIds) => {
        const next = new Set(prevIds);
        next.delete(procedureId);
        return next;
      });

      return prev.slice(0, index).concat(prev.slice(index + 1));
    });
    toast.success("Procedure removed");
  };

  const handleDeleteDiagnosis = (code: string, index: number) => {
    setDiagnoses((prev) => {
      const diagnosis = prev[index];
      if (!diagnosis) {
        return prev;
      }

      if (diagnosis.code !== code) {
        return prev;
      }

      // Remove from reviewed codes if it was reviewed
      const diagnosisId = diagnosis.id || `${diagnosis.code}-${index}`;
      setReviewedCodeIds((prevIds) => {
        const next = new Set(prevIds);
        next.delete(diagnosisId);
        return next;
      });

      return prev.slice(0, index).concat(prev.slice(index + 1));
    });
  };

  const requestDeleteDiagnosis = (code: string, index: number) => {
    if (historicCodes.has(code)) {
      setPendingHistoricCodeDelete({ code, index });
      return;
    }
    handleDeleteDiagnosis(code, index);
  };

  const confirmHistoricCodeDelete = () => {
    if (!pendingHistoricCodeDelete) return;
    handleDeleteDiagnosis(pendingHistoricCodeDelete.code, pendingHistoricCodeDelete.index);
    setPendingHistoricCodeDelete(null);
  };

  const _handleToggleDiagnosisOptional = (code: string, index: number) => {
    setDiagnoses((prev) => {
      const diagnosis = prev[index];
      if (!diagnosis) {
        return prev;
      }

      if (diagnosis.code !== code) {
        return prev;
      }

      return prev
        .slice(0, index)
        .concat(diagnosis)
        .concat(prev.slice(index + 1));
    });
  };

  const handleAddCode = (code: ClinicalCode & { type: CodeType }) => {
    if (code.type === "OPCS-4") {
      const newProcedure: Procedure = {
        code: code.code ?? "",
        description: code.description ?? "",
        orderIndex: procedures.length + 1,
        id: crypto.randomUUID(),
      };
      setProcedures((prev) => [...prev, newProcedure]);
      toast.success(`OPCS-4 code ${code.code} has been added.`);
      return;
    }

    const newDiagnosis: Diagnosis = {
      code: code.code ?? "",
      description: code.description ?? "",
      orderIndex: diagnoses.length + 1,
      id: crypto.randomUUID(),
    };
    setDiagnoses((prev) => [...prev, newDiagnosis]);
  };

  const handleInsertCode = (code: ClinicalCode & { type: CodeType }, index: number) => {
    if (code.type === "OPCS-4") {
      setProcedures((prev) => {
        const newProcedure = {
          code: code.code ?? "",
          description: code.description ?? "",
          orderIndex: 0,
          id: crypto.randomUUID(),
        };
        const updated = [...prev.slice(0, index), newProcedure, ...prev.slice(index)];
        return updated.map((proc, i) => ({ ...proc, orderIndex: i + 1 }));
      });
      toast.success(`OPCS-4 code ${code.code} inserted.`);
    } else {
      setDiagnoses((prev) => {
        const newDiagnosis = {
          code: code.code ?? "",
          description: code.description ?? "",
          orderIndex: 0,
          id: crypto.randomUUID(),
        };
        const updated = [...prev.slice(0, index), newDiagnosis, ...prev.slice(index)];
        return updated.map((diag, i) => ({ ...diag, orderIndex: i + 1 }));
      });
    }
    setInsertionPoint(null);
  };

  const renderInsertionPoint = (section: "procedures" | "diagnoses", index: number) => {
    const isExpanded = insertionPoint !== null && insertionPoint.section === section && insertionPoint.index === index;

    return (
      <div key={`insert-${section}-${index}`}>
        {isExpanded ? (
          <InlineCodeSearch
            section={section}
            onSelect={(code) => handleInsertCode(code, index)}
            onDismiss={() => setInsertionPoint(null)}
            opcsVersion={opcsVersion}
          />
        ) : (
          <InsertionPointButton section={section} index={index} onClick={() => setInsertionPoint({ section, index })} />
        )}
      </div>
    );
  };

  // Memoized onSave callback to prevent unnecessary re-renders of ReviewNotes
  // Use ref to access mutateAsync to keep callback stable
  const handleSaveNotes = useCallback(async (notes: string) => {
    await saveNotesMutateAsyncRef.current(notes);
  }, []);

  const droppedCodes = useMemo(() => {
    if (!evidenceData) return [];
    const activeCodes = new Set(diagnoses.map((d) => d.code));
    return evidenceData.filter((row) => !activeCodes.has(row.icd10Code));
  }, [evidenceData, diagnoses]);

  const aiFlagTextByProcedureCode = useMemo(
    () =>
      new Map(
        (data?.latestClassificationResult.procedures ?? []).flatMap((procedure) =>
          procedure.aiFlagText ? [[procedure.code, procedure.aiFlagText] as const] : [],
        ),
      ),
    [data?.latestClassificationResult.procedures],
  );

  // Error handling
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <div className="text-red-500 font-medium">Error loading case data</div>
          <p className="text-gray-500 mt-2">Please try refreshing the page</p>
          <Button onClick={() => window.location.reload()} className="mt-4" variant="outline">
            Refresh Page
          </Button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full flex flex-col">
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
              <div className="h-6 w-48 bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="flex items-center gap-2">
              <div className="h-8 w-32 bg-gray-200 rounded animate-pulse" />
              <div className="h-8 w-32 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        </div>
        <div className="flex-1 flex">
          <div className="w-3/4 p-6">
            <InlineLoading message="Loading case data..." />
          </div>
          <div className="w-1/4 p-4">
            <SkeletonCard />
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-gray-500">No case data found</div>
          <Button
            onClick={() => {
              allowNavigationRef.current = true;
              navigate(`${ROUTES.aiCodingOutputs.route}`);
            }}
            className="mt-4"
          >
            Return to Cases
          </Button>
        </div>
      </div>
    );
  }

  const headerSlotEl =
    pageHeaderSlot?.slotReady && pageHeaderSlot.headerSlotRef.current ? pageHeaderSlot.headerSlotRef.current : null;

  const reviewHeaderContent = (
    <div className="flex items-center justify-between w-full text-primary-foreground">
      <div className="flex items-center gap-2 sm:gap-3">
        <Button variant="ghostOnPrimary" size="sm" onClick={handleHome}>
          <Home className="h-4 w-4 mr-1 sm:mr-2" />
          <span className="hidden sm:inline">Home</span>
        </Button>
        {recentReviewedEncounters.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghostOnPrimary" size="sm" aria-label="Recent reviews">
                <History className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Recent reviews</span>
                <ChevronDown className="h-4 w-4 ml-0.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {recentReviewedEncounters.map(({ encounterId: encId, label }) => (
                <DropdownMenuItem key={encId} asChild>
                  <a
                    href={`${ROUTES.humanCodingReview.route}/${encId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`recent-review-link-${encId}`}
                  >
                    {label}
                  </a>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <TrustRulesButton
          customerId={data.customer.id}
          customerProviderId={data.customer.provider_id}
          customerLabel={data.customer?.label}
          inHeader
        />
      </div>

      {role === USER_ROLES.admin && (
        <div className="flex-1 min-w-[4rem] max-w-md mx-4">
          <MultiSelect
            options={customers.map((c) => ({ value: c.id, label: c.label }))}
            value={customerIds.map((id) => ({
              value: id,
              label: customers.find((c) => c.id === id)?.label || "",
            }))}
            onChange={handleCustomerFilterChange}
            placeholder="All customers"
            isLoading={false}
          />
        </div>
      )}

      <div className="flex items-center gap-1 sm:gap-2">
        {data.latestClassificationResult.runId && role === USER_ROLES.admin && (
          <ViewRunDropdown
            mastraRunUrl={getMastraWorkflowRunUrl(data.specialty, data.latestClassificationResult.runId)}
            langfuseTraceId={data.latestClassificationResult.langfuseTraceId}
            encounterId={encounterId}
            variant="ghostOnPrimary"
          />
        )}
        <OpenRawFilesButton encounterId={encounterId} variant="ghostOnPrimary" newTab />
        {data.latestClassificationResult.isFollowUp ? (
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-900/50 text-blue-200 border border-blue-700">
              <Clock className="h-5 w-6 mr-1" />
              Follow-up 🙅
            </div>
            <Button
              variant="ghostOnPrimary"
              onClick={handleSkip}
              disabled={isFinalizingCodes || isProcessingDoNotOutput}
              size="sm"
            >
              <ArrowRight className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Skip</span>
            </Button>
          </div>
        ) : (
          <>
            {canAuditCodes && (
              <Button
                variant="ghostOnPrimary"
                size="sm"
                onClick={() => setShowAuditDialog(true)}
                disabled={diagnoses.length === 0 && procedures.length === 0}
                title="Check your codes against this trust's coding rules"
              >
                <ScrollText className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Audit my codes</span>
              </Button>
            )}
            <Button
              variant="primaryInverse"
              onClick={openFinalizeConfirm}
              disabled={isFinalizingCodes || isProcessingDoNotOutput || isSaveInFlight}
              size="sm"
            >
              <CheckCircle className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Finalise</span>
            </Button>
            <Button
              variant="ghostOnPrimary"
              onClick={handleSkip}
              disabled={isFinalizingCodes || isProcessingDoNotOutput}
              size="sm"
            >
              <ArrowRight className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Skip</span>
            </Button>
            <Button
              variant="ghost"
              onClick={handleDoNotOutput}
              disabled={isFinalizingCodes || isProcessingDoNotOutput}
              size="sm"
              className="text-red-300 hover:bg-red-900/50 hover:text-red-100"
            >
              <XCircle className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Reject</span>
            </Button>
            <SaveStateBadge state={saveState} tone="onPrimary" />
          </>
        )}
        <QueueAlmostThroughPill />
        <ReviewStateBadge status={data.reviewStatus} outcome={data.reviewOutcome} />
      </div>
    </div>
  );

  return (
    <div
      className={`h-full flex flex-col ${isFlashing ? "animate-customer-flash" : ""}`}
      style={{ backgroundColor: getReviewPageBackground(data.customer?.provider_id ?? null) }}
    >
      {headerSlotEl ? (
        createPortal(reviewHeaderContent, headerSlotEl)
      ) : (
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-2 sm:px-4 py-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <Button variant="outline" onClick={handleHome} size="sm">
                <Home className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Home</span>
              </Button>
              {recentReviewedEncounters.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" aria-label="Recent reviews">
                      <History className="h-4 w-4 mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">Recent reviews</span>
                      <ChevronDown className="h-4 w-4 ml-0.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {recentReviewedEncounters.map(({ encounterId: encId, label }) => (
                      <DropdownMenuItem key={encId} asChild>
                        <a
                          href={`${ROUTES.humanCodingReview.route}/${encId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`recent-review-link-${encId}`}
                        >
                          {label}
                        </a>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <TrustRulesButton
                customerId={data.customer.id}
                customerProviderId={data.customer.provider_id}
                customerLabel={data.customer?.label}
              />
            </div>
            {role === USER_ROLES.admin && (
              <div className="flex-1 min-w-[4rem] max-w-md mx-4">
                <MultiSelect
                  options={customers.map((c) => ({ value: c.id, label: c.label }))}
                  value={customerIds.map((id) => ({
                    value: id,
                    label: customers.find((c) => c.id === id)?.label || "",
                  }))}
                  onChange={handleCustomerFilterChange}
                  placeholder="All customers"
                  isLoading={false}
                />
              </div>
            )}
            <div className="flex items-center gap-1 sm:gap-2">
              {data.latestClassificationResult.runId && role === USER_ROLES.admin && (
                <ViewRunDropdown
                  mastraRunUrl={getMastraWorkflowRunUrl(data.specialty, data.latestClassificationResult.runId)}
                  langfuseTraceId={data.latestClassificationResult.langfuseTraceId}
                  encounterId={encounterId}
                />
              )}
              <OpenRawFilesButton encounterId={encounterId} newTab />
              {data.latestClassificationResult.isFollowUp ? (
                <div className="flex items-center gap-2">
                  <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                    <Clock className="h-5 w-6 mr-1" />
                    Follow-up 🙅
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleSkip}
                    disabled={isFinalizingCodes || isProcessingDoNotOutput}
                    size="sm"
                  >
                    <ArrowRight className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">Skip</span>
                  </Button>
                </div>
              ) : (
                <>
                  {canAuditCodes && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAuditDialog(true)}
                      disabled={diagnoses.length === 0 && procedures.length === 0}
                      title="Check your codes against this trust's coding rules"
                    >
                      <ScrollText className="h-4 w-4 mr-1" />
                      <span className="hidden sm:inline">Audit my codes</span>
                    </Button>
                  )}
                  <Button
                    variant="default"
                    onClick={openFinalizeConfirm}
                    disabled={isFinalizingCodes || isProcessingDoNotOutput || isSaveInFlight}
                    size="sm"
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">Finalise</span>
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleSkip}
                    disabled={isFinalizingCodes || isProcessingDoNotOutput}
                    size="sm"
                  >
                    <ArrowRight className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">Skip</span>
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleDoNotOutput}
                    disabled={isFinalizingCodes || isProcessingDoNotOutput}
                    size="sm"
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    <span className="hidden sm:inline">Reject</span>
                  </Button>
                  <SaveStateBadge state={saveState} tone="light" />
                </>
              )}
              <QueueAlmostThroughPill />
              <ReviewStateBadge status={data.reviewStatus} outcome={data.reviewOutcome} />
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay for finalize/do not output actions */}
      <LoadingOverlay
        isLoading={isFinalizingCodes || isProcessingDoNotOutput}
        message={isFinalizingCodes ? "Finalizing codes..." : "Processing request..."}
      />

      {/* Main Content - Updated width split to 70/30 */}
      <div className="flex-1 flex flex-row overflow-hidden">
        <div
          className="border-b lg:border-b-0 border-r border-gray-200 min-w-[350px] flex flex-col h-full min-h-0"
          style={{ width: `${pdfColumn.width}%` }}
        >
          <ScrollArea className={`h-full min-h-0 ${RADIX_SCROLL_CONTENT_BLOCK}`}>
            <div className="p-2 sm:p-4">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className="flex items-center space-x-2">
                  {data.latestClassificationResult.humanFlagForReview && (
                    <span title="Flagged for Review by human">🙋</span>
                  )}
                  {data.latestClassificationResult.aiFlagForReview && role !== USER_ROLES.clinical_coder && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-default">🤖🚩</span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[min(90vw,20rem)] break-words whitespace-pre-line">
                          {role === USER_ROLES.admin && data.latestClassificationResult.aiFlagForReviewText
                            ? data.latestClassificationResult.aiFlagForReviewText
                            : "Flagged by AI for review"}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {!data.latestClassificationResult.humanFlagForReview &&
                    !(data.latestClassificationResult.aiFlagForReview && role !== USER_ROLES.clinical_coder) && (
                      <span className="text-gray-400" title="Not flagged">
                        —
                      </span>
                    )}
                  {/* Microscope icon for specimens taken */}
                  {data.latestClassificationResult.endoscopyResult?.wasSpecimenOrHistologyTaken && (
                    <span title="Specimens taken">
                      <Microscope className="inline h-5 w-5 text-purple-700" />
                    </span>
                  )}
                  <span className="text-sm text-gray-600 font-medium" title="Patient age">
                    Age: {data.patientAge}
                  </span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-sm text-gray-600 font-medium" title="Patient sex">
                          Sex: {data.patientSex || "U"}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {data.patientSex === "U" || !data.patientSex
                          ? "U = Unknown"
                          : data.patientSex === "M"
                            ? "M = Male"
                            : "F = Female"}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {data.dischargeDatetime && (
                    <span className="text-sm text-gray-600 font-medium" title="Observation datetime">
                      Encounter: {dayjs(data.admitDatetime).tz().format("DD/MM/YYYY")}
                      {" - "}
                      {dayjs(data.dischargeDatetime).tz().format("DD/MM/YYYY")}
                    </span>
                  )}
                </div>
                {(diagnoses.length > 0 || procedures.length > 0) &&
                data.latestClassificationResult.outcome !== "error" ? (
                  <ScoreDisplay
                    aiDiagnoses={data.latestClassificationResult.diagnoses.map((d) => d.code)}
                    aiProcedures={data.latestClassificationResult.procedures.map((p) => p.code)}
                    aiFlaggedForReview={data.latestClassificationResult.aiFlagForReview}
                    humanDiagnoses={diagnoses.map((d) => d.code)}
                    humanProcedures={procedures.map((p) => p.code)}
                  />
                ) : null}
              </div>

              {/* Medical Documents Display */}
              <ProcessedFilesTabs
                files={reportFiles || []}
                isLoading={isLoadingFiles}
                error={fileError}
                allHighlights={allHighlights}
                activeFileIdentifier={activeFileIdentifier}
                indications={indications}
                scrollTrigger={scrollTrigger}
              />
            </div>
          </ScrollArea>
        </div>

        {data.latestClassificationResult.isFollowUp ? null : (
          <>
            <ResizeHandle
              onMouseDown={pdfColumn.handleMouseDown}
              isDragging={pdfColumn.isDragging}
              ariaLabel="Resize review columns"
            />
            <div className="flex-1 min-w-[200px] min-h-0 h-full bg-white">
              <ScrollArea className={`h-full ${RADIX_SCROLL_CONTENT_BLOCK}`}>
                <div className="p-2 sm:p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        aria-label="View original AI codes"
                        title="View original AI codes"
                        onClick={() => setShowAICodesDialog(true)}
                      >
                        <span className="text-base">🤖</span>
                      </Button>
                      <h2 className="text-lg font-semibold">Clinical Codes</h2>
                    </div>
                    <AddClinicalCodeDialog onSelect={handleAddCode} opcsVersion={opcsVersion} />
                  </div>

                  <DragDropContext onDragEnd={handleDragEnd}>
                    {/* Procedures (OPCS-4) - hidden when timeline is open */}
                    {!timelinePanel.activeCode && procedures.length > 0 && (
                      <div className="mb-2">
                        <h3 className="text-sm font-medium mb-1 text-gray-700">
                          Procedures ({opcsVersion ? getOpcsVersionLabel(opcsVersion) : "OPCS-4"})
                        </h3>
                        <Droppable droppableId="procedures" type="procedures">
                          {(provided, droppableSnapshot) => (
                            <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-1">
                              {procedures.map((procedure, index) => {
                                const procedureId = procedure.id || `${procedure.code}-${index}`;
                                const aiFlagText = aiFlagTextByProcedureCode.get(procedure.code);
                                return (
                                  <div key={procedureId}>
                                    {!droppableSnapshot.isDraggingOver && renderInsertionPoint("procedures", index)}
                                    <Draggable draggableId={procedureId} index={index}>
                                      {(provided, snapshot) => (
                                        <div className="flex items-center gap-2">
                                          <Checkbox
                                            checked={reviewedCodeIds.has(procedureId)}
                                            onCheckedChange={(checked) => {
                                              setReviewedCodeIds((prev) => {
                                                const next = new Set(prev);
                                                if (checked) {
                                                  next.add(procedureId);
                                                } else {
                                                  next.delete(procedureId);
                                                }
                                                return next;
                                              });
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                          <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            className={`flex-1 p-1.5 border rounded-md transition-colors ${
                                              snapshot.isDragging ? "shadow-lg" : ""
                                            } bg-emerald-50 border-emerald-200 hover:bg-emerald-100`}
                                          >
                                            <div className="flex items-center justify-between mb-1">
                                              <div className="flex items-center gap-2">
                                                <span className="font-mono text-sm font-medium">{procedure.code}</span>
                                                <InlineFlags
                                                  flags={procedureInlineFlags(
                                                    { ...procedure, aiFlagText },
                                                    index,
                                                    data?.specialty,
                                                  )}
                                                />
                                              </div>
                                              <div className="flex items-center gap-1">
                                                <Button
                                                  size="sm"
                                                  variant="ghost"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteProcedure(procedure.code, index);
                                                  }}
                                                  className="h-5 w-5 p-0 text-red-500 hover:text-red-700"
                                                  aria-label={`Delete procedure ${procedure.code}`}
                                                >
                                                  <Trash2 className="h-3 w-3" />
                                                </Button>
                                              </div>
                                            </div>
                                            {/* Display truncated description with tooltip */}
                                            <TooltipProvider>
                                              <Tooltip>
                                                <TooltipTrigger asChild>
                                                  <div className="text-xs text-gray-600 line-clamp-2 cursor-help">
                                                    {procedure.description}
                                                  </div>
                                                </TooltipTrigger>
                                                <TooltipContent className="max-w-xs">
                                                  <p>{procedure.description}</p>
                                                </TooltipContent>
                                              </Tooltip>
                                            </TooltipProvider>
                                          </div>
                                        </div>
                                      )}
                                    </Draggable>
                                  </div>
                                );
                              })}
                              {provided.placeholder}
                              {!droppableSnapshot.isDraggingOver &&
                                renderInsertionPoint("procedures", procedures.length)}
                            </div>
                          )}
                        </Droppable>
                      </div>
                    )}

                    {/* All Diagnoses (ICD-10) - Combined primary and secondary */}
                    {diagnoses.length > 0 && (
                      <div className="mb-2">
                        <h3 className="text-sm font-medium mb-1 text-gray-700">Diagnosis (ICD-10)</h3>
                        {daggerAsteriskCheckFailed && <OrphanedAsteriskCheckFailedNotice />}
                        {restrictionCheckFailed && <RestrictionCheckFailedNotice />}
                        <Droppable droppableId="diagnoses" type="diagnoses">
                          {(provided, droppableSnapshot) => (
                            <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-1">
                              {diagnoses.map((diagnosis, index) => {
                                const diagnosisId = diagnosis.id || `${diagnosis.code}-${index}`;
                                return (
                                  <div key={diagnosisId}>
                                    {!droppableSnapshot.isDraggingOver && renderInsertionPoint("diagnoses", index)}
                                    <Draggable draggableId={diagnosisId} index={index}>
                                      {(provided, snapshot) => (
                                        <div className="flex items-center gap-2">
                                          <Checkbox
                                            checked={reviewedCodeIds.has(diagnosisId)}
                                            onCheckedChange={(checked) => {
                                              setReviewedCodeIds((prev) => {
                                                const next = new Set(prev);
                                                if (checked) {
                                                  next.add(diagnosisId);
                                                } else {
                                                  next.delete(diagnosisId);
                                                }
                                                return next;
                                              });
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                          <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            className={`flex-1 p-1.5 border rounded-md transition-all duration-200 ${
                                              snapshot.isDragging ? "shadow-lg" : ""
                                            } ${
                                              evidenceHighlight.focusedCode === diagnosis.code
                                                ? "bg-sky-50 border-sky-300 shadow-md ring-1 ring-sky-200"
                                                : ""
                                            }`}
                                          >
                                            <div className="flex items-center justify-between mb-1">
                                              <div className="flex items-center gap-2">
                                                <span className="font-mono text-sm font-medium">{diagnosis.code}</span>
                                                <InlineFlags flags={flagsByIndex[index] ?? []} />
                                              </div>
                                              <div className="flex items-center gap-1">
                                                {index === 0 && (
                                                  <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                                                    Primary
                                                  </span>
                                                )}
                                                {evidenceByCode.has(diagnosis.code) && (
                                                  <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    className={`h-5 w-5 p-0 ${timelinePanel.activeCode === diagnosis.code ? "text-purple-500" : "text-gray-400 hover:text-gray-600"}`}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleOpenTimeline(diagnosis.code);
                                                    }}
                                                    aria-label={`View timeline for ${diagnosis.code}`}
                                                  >
                                                    <History className="h-3 w-3" />
                                                  </Button>
                                                )}
                                                <Button
                                                  size="sm"
                                                  variant="ghost"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    requestDeleteDiagnosis(diagnosis.code, index);
                                                  }}
                                                  className="h-5 w-5 p-0 text-red-500 hover:text-red-700"
                                                  aria-label={`Delete diagnosis ${diagnosis.code}`}
                                                >
                                                  <Trash2 className="h-3 w-3" />
                                                </Button>
                                              </div>
                                            </div>
                                            {/* Display truncated description with tooltip */}
                                            {diagnosis.description && (
                                              <TooltipProvider>
                                                <Tooltip>
                                                  <TooltipTrigger asChild>
                                                    <div className="text-xs text-gray-600 line-clamp-2 cursor-help">
                                                      {diagnosis.description}
                                                    </div>
                                                  </TooltipTrigger>
                                                  <TooltipContent className="max-w-xs">
                                                    <p>{diagnosis.description}</p>
                                                  </TooltipContent>
                                                </Tooltip>
                                              </TooltipProvider>
                                            )}
                                            <DiagnosisSourceText
                                              code={diagnosis.code}
                                              quotes={uniqueQuoteTexts(
                                                evidenceByCode.get(diagnosis.code)?.timeline ?? [],
                                              )}
                                              isFocused={evidenceHighlight.focusedCode === diagnosis.code}
                                              onToggleHighlight={() => handleToggleHighlight(diagnosis.code)}
                                            />
                                          </div>
                                        </div>
                                      )}
                                    </Draggable>
                                  </div>
                                );
                              })}
                              {provided.placeholder}
                              {!droppableSnapshot.isDraggingOver && renderInsertionPoint("diagnoses", diagnoses.length)}
                            </div>
                          )}
                        </Droppable>
                      </div>
                    )}
                  </DragDropContext>

                  {/* Dropped codes section */}
                  {droppedCodes.length > 0 && (
                    <div className="mt-2">
                      <button
                        className="w-full flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 py-1"
                        onClick={toggleDropped}
                      >
                        <div className="flex-1 h-px bg-gray-200" />
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 border border-gray-200">
                          {showDropped ? "Hide" : "Show"} dropped ({droppedCodes.length})
                        </span>
                        <div className="flex-1 h-px bg-gray-200" />
                      </button>
                      {showDropped && (
                        <div className="mt-2 space-y-1 opacity-50">
                          {droppedCodes.map((dropped) => (
                            <div
                              key={dropped.icd10Code}
                              className="p-1.5 border border-dashed border-gray-300 rounded-md flex items-center gap-2"
                            >
                              <div className="flex-1 min-w-0">
                                <span className="font-mono text-sm text-gray-400 line-through">
                                  {dropped.icd10Code}
                                </span>
                                <div className="text-xs text-gray-400 truncate">{dropped.description}</div>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                className={`h-5 w-5 p-0 ${timelinePanel.activeCode === dropped.icd10Code ? "text-purple-500" : "text-gray-400 hover:text-gray-600"}`}
                                onClick={() => handleOpenTimeline(dropped.icd10Code)}
                                aria-label={`View timeline for dropped ${dropped.icd10Code}`}
                              >
                                <History className="h-3 w-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {unmatchedLeadTerms && unmatchedLeadTerms.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      <p className="text-xs font-medium text-muted-foreground">Unmatched terms</p>
                      {unmatchedLeadTerms.map((term, i) => (
                        <UnmatchedLeadTermItem
                          key={`${term.leadTerm}-${i}`}
                          term={term}
                          onToggleHighlight={handleToggleHighlight}
                          isFocused={evidenceHighlight.focusedCode === `unmatched::${term.leadTerm}`}
                        />
                      ))}
                    </div>
                  )}

                  {/* No codes message */}
                  {procedures.length === 0 && diagnoses.length === 0 && (
                    <div className="text-gray-500 text-center py-8">
                      <div className="mb-2">No codes assigned by AI</div>
                      <div className="text-sm">This case may require manual coding</div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>
          </>
        )}

        {/* Timeline Panel - third column */}
        {timelinePanel.activeCode && evidenceByCode.has(timelinePanel.activeCode) && (
          <div className="w-3/10 min-w-[280px] transition-all duration-200">
            <DiagnosisTimeline
              code={timelinePanel.activeCode}
              description={evidenceByCode.get(timelinePanel.activeCode)!.description}
              timeline={evidenceByCode.get(timelinePanel.activeCode)!.timeline}
              onClose={handleCloseTimeline}
              onEvidenceClick={handleTimelineEvidenceClick}
            />
          </div>
        )}
      </div>

      {/* Confirmation Dialogs */}
      <ConfirmationDialog
        isOpen={showFinalizeConfirm}
        onClose={dismissFinalizeConfirm}
        onConfirm={confirmFinalize}
        title="Finalize Codes"
        description="Are you sure you want to finalize these clinical codes? This action will complete the review process."
        confirmText="Finalize"
        variant="success"
        isLoading={isFinalizingCodes}
      />

      <ConfirmationDialog
        isOpen={pendingHistoricCodeDelete !== null}
        onClose={() => setPendingHistoricCodeDelete(null)}
        onConfirm={confirmHistoricCodeDelete}
        title="Remove this code?"
        description="This is a confirmed diagnosis from a previous episode. Are you sure you want to remove it?"
        confirmText="Remove"
        variant="destructive"
      />

      <CodeRuleAuditDialog
        open={showAuditDialog}
        onOpenChange={setShowAuditDialog}
        encounterId={encounterId}
        diagnoses={diagnoses.map((d) => d.code)}
        procedures={procedures.map((p) => p.code)}
        onFinalise={openFinalizeConfirm}
        finaliseDisabled={isFinalizingCodes || isProcessingDoNotOutput || isSaveInFlight}
      />

      {/* Do Not Output Modal */}
      <DoNotOutputModal
        isOpen={showDoNotOutputModal}
        onClose={() => setShowDoNotOutputModal(false)}
        onConfirm={handleDoNotOutputConfirm}
        isLoading={isProcessingDoNotOutput}
      />

      {/* Navigation Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showNavigationConfirm}
        onClose={() => {
          setShowNavigationConfirm(false);
          pendingNavigationRef.current = null;
        }}
        onConfirm={() => {
          setShowNavigationConfirm(false);
          allowNavigationRef.current = true;
          if (pendingNavigationRef.current) {
            navigate(pendingNavigationRef.current);
            pendingNavigationRef.current = null;
          } else {
            // Browser back/forward - allow it by going back
            window.history.back();
          }
          // Reset after a short delay to allow navigation to complete
          setTimeout(() => {
            allowNavigationRef.current = false;
          }, 100);
        }}
        title="Leave Page?"
        description="Are you sure you want to leave this page? (your changes have be saved)"
        confirmText="Leave"
        cancelText="Stay"
        variant="default"
      />

      {/* Original AI Codes Dialog */}
      <Dialog open={showAICodesDialog} onOpenChange={setShowAICodesDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Original AI Codes</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium mb-2 text-gray-700">
                Procedures ({opcsVersion ? getOpcsVersionLabel(opcsVersion) : "OPCS-4"})
              </h3>
              {data?.latestClassificationResult.procedures && data.latestClassificationResult.procedures.length > 0 ? (
                <ul className="list-disc pl-5 space-y-1">
                  {data.latestClassificationResult.procedures.map((p, idx) => (
                    <li key={`${p.code}-${idx}`} className="text-sm">
                      <span className="font-mono font-medium">{p.code}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-gray-500">No AI procedures</div>
              )}
            </div>
            <div>
              <h3 className="text-sm font-medium mb-2 text-gray-700">Diagnoses (ICD-10)</h3>
              {data?.latestClassificationResult.diagnoses && data.latestClassificationResult.diagnoses.length > 0 ? (
                <ul className="list-disc pl-5 space-y-1">
                  {data.latestClassificationResult.diagnoses.map((d, idx) => (
                    <li key={`${d.code}-${idx}`} className="text-sm">
                      <span className="font-mono font-medium">{d.code}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-gray-500">No AI diagnoses</div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Notes - Fixed at bottom */}
      {encounterId && (
        <ReviewNotes
          caseId={encounterId}
          initialNotes={data?.notes || ""}
          onSave={handleSaveNotes}
          audit={canAuditCodes ? auditState : undefined}
        />
      )}
    </div>
  );
};

const HumanCodingReviewEnsureReviewExists = () => {
  const { id: encounterId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fromAdminReview = searchParams.get("fromAdminReview") === "true";
  const classificationResultIdFromUrl = searchParams.get("classificationResultId") ?? undefined;
  const queryClient = useQueryClient();
  const [ensureFallbackTriggered, setEnsureFallbackTriggered] = useState(false);

  const { data, isLoading, error } = useEncounterHumanCodingReviewData(encounterId, classificationResultIdFromUrl);
  const { ensureReviewExistsMutation } = useEncounterHumanReviewMutations();

  useEffect(() => {
    if (
      !encounterId ||
      !error ||
      !(error instanceof HumanReviewNotFoundError) ||
      !fromAdminReview ||
      ensureFallbackTriggered
    ) {
      return;
    }
    setEnsureFallbackTriggered(true);
    const run = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;
      try {
        await ensureReviewExistsMutation.mutateAsync({ encounterId });
        await queryClient.invalidateQueries({
          queryKey: ["encounter-human-coding-review", encounterId, classificationResultIdFromUrl],
        });
      } catch (err) {
        console.error("Failed to ensure review exists:", err);
      }
    };
    run();
  }, [
    encounterId,
    classificationResultIdFromUrl,
    error,
    fromAdminReview,
    ensureFallbackTriggered,
    ensureReviewExistsMutation,
    queryClient,
  ]);

  if (!encounterId) {
    return (
      <div className="flex items-center justify-center min-h-[16rem]">
        <div className="text-center max-w-md">
          <div className="font-medium text-foreground mb-2">No encounter selected</div>
          <p className="text-muted-foreground text-sm">
            Select an encounter from the worklist to start a human coding review.
          </p>
          <Button variant="default" className="mt-4" onClick={() => navigate(ROUTES.aiCodingOutputs.route)}>
            Go to human review worklist
          </Button>
        </div>
      </div>
    );
  }
  if (data) {
    return (
      <HumanCodingReview
        encounterId={encounterId}
        reviewId={data.reviewId}
        classificationResultId={data.latestClassificationResult.id}
      />
    );
  }
  if (isLoading) {
    return (
      <div className="flex min-h-[12rem] items-center justify-center">
        <InlineLoading message="Loading case data..." />
      </div>
    );
  }
  if (error instanceof HumanReviewNotFoundError) {
    if (fromAdminReview) {
      return (
        <div className="flex min-h-[12rem] items-center justify-center">
          <InlineLoading message="Setting up review..." />
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center min-h-[16rem]">
        <div className="text-center max-w-md">
          <XCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <div className="font-medium text-foreground">No review found for this encounter</div>
          <p className="text-muted-foreground mt-2 text-sm">
            This encounter does not have a review assigned to you. Open it from the worklist so a review can be created,
            or return to the case list.
          </p>
          <Button variant="outline" className="mt-4" onClick={() => window.history.back()}>
            Go back
          </Button>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <div className="text-red-500 font-medium">Error loading case data</div>
          <p className="text-gray-500 mt-2">Please try refreshing the page</p>
          <Button onClick={() => window.location.reload()} className="mt-4" variant="outline">
            Refresh Page
          </Button>
        </div>
      </div>
    );
  }
  return null;
};

export default HumanCodingReviewEnsureReviewExists;
